import { useEffect } from 'react';

/**
 * useKeyboardShortcut — register a global keyboard shortcut.
 *
 * Conventions in Turn Connect:
 *   /       focus global search
 *   ?       show keyboard shortcut help overlay
 *   Esc     close the topmost modal / popover
 *   g + w   navigate to Work Orders
 *   g + d   navigate to Dashboard
 *   g + r   navigate to Reports
 *
 * Usage (single key):
 *
 *   useKeyboardShortcut('/', () => searchInputRef.current?.focus(), {
 *     ignoreInFields: true,  // don't fire while user is typing
 *   });
 *
 * Usage (chord — second keypress within window):
 *
 *   useKeyboardChord('g', { w: () => goto('workorders'), d: () => goto('dashboard') });
 */

interface ShortcutOptions {
  /** Don't fire when focus is in an input/textarea/contenteditable. Default true. */
  ignoreInFields?: boolean;
  /** Require Meta (Cmd/Ctrl)? Default false. */
  meta?: boolean;
  /** Require Shift? Default false. */
  shift?: boolean;
  /** Require Alt? Default false. */
  alt?: boolean;
  /** Enable the shortcut. Default true. */
  enabled?: boolean;
  /**
   * Don't fire while a modal dialog (`[aria-modal="true"]`) is open.
   * Plain-letter shortcuts are page-level affordances — firing them under
   * an open modal opens overlays over (or navigates underneath) the modal.
   * Defaults to true for plain shortcuts and false for meta shortcuts,
   * which act as app-level commands (e.g. Ctrl+K must still toggle the
   * palette closed).
   *
   * The command palette / shortcuts-help overlays deliberately do NOT set
   * aria-modal, so global shortcuts keep working while they are open.
   */
  ignoreWhenModalOpen?: boolean;
}

function isTextInput(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

/** True while any modal dialog (ui Modal — wizards, viewers, etc.) is mounted. */
function isModalDialogOpen(): boolean {
  return !!document.querySelector('[aria-modal="true"]');
}

export function useKeyboardShortcut(key: string, handler: (e: KeyboardEvent) => void, opts: ShortcutOptions = {}): void {
  useEffect(() => {
    if (opts.enabled === false) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== key) return;
      // Modifier discipline: meta shortcuts require Ctrl/Cmd; plain
      // shortcuts must NOT fire with Ctrl/Cmd/Alt held. (Browser-reserved
      // combos like Ctrl+N / Ctrl+T never reach the page at all — plain
      // letters are the only reliable single-key shortcuts on the web.)
      if (opts.meta && !(e.metaKey || e.ctrlKey)) return;
      if (!opts.meta && (e.metaKey || e.ctrlKey)) return;
      if (!!opts.shift !== e.shiftKey) return;
      if (!!opts.alt !== e.altKey) return;
      const ignoreInFields = opts.ignoreInFields ?? true;
      if (ignoreInFields && isTextInput(document.activeElement)) return;
      const ignoreWhenModalOpen = opts.ignoreWhenModalOpen ?? !opts.meta;
      if (ignoreWhenModalOpen && isModalDialogOpen()) return;
      handler(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [key, handler, opts.meta, opts.shift, opts.alt, opts.ignoreInFields, opts.enabled, opts.ignoreWhenModalOpen]);
}

/**
 * useKeyboardChord — register a g+X style two-key sequence.
 *
 * After the prefix key fires, any of the second-key handlers in the
 * map can match within 1.5s. Useful for "g w" → workorders style
 * shortcuts that don't conflict with letter typing.
 */
export function useKeyboardChord(
  prefix: string,
  handlers: Record<string, (e: KeyboardEvent) => void>,
  opts: { windowMs?: number; ignoreInFields?: boolean } = {}
): void {
  useEffect(() => {
    const windowMs = opts.windowMs ?? 1500;
    let armedAt = 0;
    const onKey = (e: KeyboardEvent) => {
      const inField = isTextInput(document.activeElement);
      if (opts.ignoreInFields !== false && inField) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Chords are plain-letter page affordances — never fire under a modal.
      if (isModalDialogOpen()) {
        armedAt = 0;
        return;
      }
      const now = Date.now();
      if (e.key === prefix && armedAt === 0) {
        armedAt = now;
        return;
      }
      if (armedAt > 0 && now - armedAt <= windowMs) {
        const h = handlers[e.key];
        armedAt = 0;
        if (h) {
          e.preventDefault();
          h(e);
        }
        return;
      }
      armedAt = 0;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // We deliberately rebuild only when handler keys change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix, opts.windowMs, opts.ignoreInFields, JSON.stringify(Object.keys(handlers))]);
}
